import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  DirectionsCar as DirectionsCarIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigation, VIEWS } from '../../context/NavigationContext';
import { puedeModoGlobal } from '../../utils/rolesModoGlobal';
import ManageUsersDialog from '../VehicleLog/Dialogs/ManageUsersDialog';
import { BRANDING } from '../../branding';

const UserHeader = () => {
  const { user, logout, clienteInfo, selectCliente } = useAuth();
  const { navigate } = useNavigation();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const [volviendo, setVolviendo] = useState(false);

  // Botón notorio para salir de la sucursal actual y ver todas combinadas de
  // nuevo — antes era una flechita diminuta junto al selector del sidebar,
  // fácil de no ver. Aquí, en el encabezado (siempre visible, fuera del área
  // que hace scroll), solo aparece si el rol tiene modo global y se está
  // dentro de una sucursal ahora mismo.
  const enSucursal = user?.sucursal_id != null;
  const puedeVolverGlobal = enSucursal && puedeModoGlobal(user);
  const volverAGlobal = async () => {
    if (volviendo || !user?.cliente_id) return;
    setVolviendo(true);
    try {
      const res = await selectCliente(user.cliente_id, null);
      if (res.success) navigate(VIEWS.DASHBOARD);
    } finally {
      setVolviendo(false);
    }
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };


  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleOpenManageUsers = () => {
    setManageUsersOpen(true);
    handleUserMenuClose();
  };

  const getUserRole = (role) => {
    const roles = {
      "guard": "Seguridad",
      "admin": "Administrador",
      "supervisor": "Supervisor",
      "operator": "Operador"
    }
    return roles[role]
  };

  return (
    <AppBar
      position="static"
      sx={{
        background: '#282626ff',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 0
      }}
    >
      <Toolbar sx={{ py: 1.5 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2, marginLeft: "3rem" }}>
          <Box
            component="img"
            src={BRANDING.logoHeader}
            alt={BRANDING.nombre}
            sx={{
              height: '48px',
              width: 'auto',
              objectFit: "contain",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
          />
          {(clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO) && (
            <Chip
              label={clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
          )}
          {clienteInfo?.sucursal?.nombre && (
            <Chip
              label={clienteInfo.sucursal.nombre}
              size="small"
              sx={{ bgcolor: 'rgba(59,130,246,0.25)', color: 'white', fontWeight: 600 }}
            />
          )}
          {puedeVolverGlobal && (
            <Button
              onClick={volverAGlobal}
              disabled={volviendo}
              startIcon={volviendo ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PublicIcon />}
              size="small"
              sx={{
                bgcolor: '#F59E0B', color: 'white', fontWeight: 700, textTransform: 'none',
                borderRadius: 2, px: 1.5,
                '&:hover': { bgcolor: '#D97706' },
              }}
            >
              Volver a global
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Información del Usuario */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ textAlign: 'right', mr: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                {user?.name} {user?.lastname}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {getUserRole(user?.role)}
              </Typography>
            </Box>

            <Avatar
              onClick={handleUserMenuClick}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'rgba(255,255,255,0.2)',
                cursor: 'pointer',
                border: '2px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                  border: '2px solid rgba(255,255,255,0.5)'
                }
              }}
            >
              {user?.name?.charAt(0)}{user?.lastname?.charAt(0)}
            </Avatar>
          </Box>

          {/* Menú de Usuario */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
              }
            }}
          >
            {/* <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {user?.name} {user?.lastname}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getUserRole(user?.role)}
              </Typography>
            </Box>
            
            <Divider />
            
            <MenuItem onClick={handleUserMenuClose}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mi Perfil</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={handleUserMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Configuración</ListItemText>
            </MenuItem>
            
            <Divider /> */}
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      <ManageUsersDialog
        open={manageUsersOpen}
        onClose={() => setManageUsersOpen(false)}
      />

    </AppBar>
  );
};

export default UserHeader;